import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  FaArrowRight,
  FaBullseye,
  FaChartLine,
  FaClipboardList,
  FaFileAlt,
  FaGem,
  FaGlobeAmericas,
  FaHandshake,
  FaHeadphones,
  FaIndustry,
  FaLock,
  FaShieldAlt,
  FaShoppingBag,
  FaShoppingCart,
  FaTruck,
  FaUserFriends,
  FaUsers,
  FaWarehouse,
} from "react-icons/fa";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import { fetchPublicCmsPage } from "../../services/cms.service";
import { buildAboutCmsSections } from "../../utils/aboutPageCms";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/about.scss";

const WHO_CARD_ICONS = {
  vision: FaUsers,
  mission: FaBullseye,
  values: FaGem,
  commitment: FaShieldAlt,
};

const STATS = [
  { icon: FaUserFriends, value: "25K+", label: "Registered Businesses" },
  { icon: FaShoppingCart, value: "1.2M+", label: "Products & Services" },
  { icon: FaFileAlt, value: "50K+", label: "RFQs Processed" },
  { icon: FaGlobeAmericas, value: "120+", label: "Countries Served" },
  { icon: FaHandshake, value: "98%", label: "Customer Satisfaction" },
];

const OFFERS = [
  {
    icon: FaShoppingBag,
    title: "Marketplace",
    desc: "Discover verified sellers, products, and opportunities across industries.",
    cta: "Explore Now",
    to: "/marketplace/products",
  },
  {
    icon: FaIndustry,
    title: "Contract Manufacturing",
    desc: "Find manufacturing partners that match your production requirements.",
    cta: "Learn More",
    to: "/contract-manufacturers",
  },
  {
    icon: FaWarehouse,
    title: "Infrastructure Leasing",
    desc: "Lease warehouses, factories, and industrial spaces with confidence.",
    cta: "Explore Now",
    to: "/infrastructure-leasing/browse",
  },
  {
    icon: FaClipboardList,
    title: "Procure to Pay",
    desc: "Streamline sourcing, approvals, and payments in one connected flow.",
    cta: "Learn More",
    to: "/procurement-services",
  },
  {
    icon: FaTruck,
    title: "Logistics Solutions",
    desc: "Move goods securely with trusted logistics and freight partners.",
    cta: "Explore Now",
    to: "/logistics-providers",
  },
];

const WHY_CHOOSE = [
  {
    icon: FaShieldAlt,
    title: "Verified & Trusted",
    desc: "Work with screened businesses and verified partner profiles.",
  },
  {
    icon: FaHeadphones,
    title: "Dedicated Support",
    desc: "Get help from a support team that understands B2B operations.",
  },
  {
    icon: FaLock,
    title: "Secure & Transparent",
    desc: "Clear processes, protected data, and accountable workflows.",
  },
  {
    icon: FaChartLine,
    title: "Growth Focused",
    desc: "Tools and networks designed to scale your business faster.",
  },
];

export default function About() {
  const [cmsPage, setCmsPage] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchPublicCmsPage("ABOUT_US");
        if (!cancelled) setCmsPage(data);
      } catch {
        if (!cancelled) setCmsPage(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const cms = useMemo(() => buildAboutCmsSections(cmsPage), [cmsPage]);
  const heroBackgroundSrc = cms.heroBackgroundUrl
    ? resolvePublicFileUrl(cms.heroBackgroundUrl)
    : "";
  const heroCityOverlaySrc = cms.heroCityOverlayUrl
    ? resolvePublicFileUrl(cms.heroCityOverlayUrl)
    : "";
  const isCustomHeroImage =
    Boolean(cms.heroBackgroundUrl) &&
    !String(cms.heroBackgroundUrl).includes("images.unsplash.com");

  return (
    <div className="hub-about-page">
      <B2BHubHomeHeader />

      <main className="hub-about-page__main">
        <section className="hub-about-hero" aria-labelledby="hub-about-hero-title">
          <div className="hub-about-hero__pattern" aria-hidden />
          <div className="hub-about-hero__inner">
            <div className="hub-about-hero__copy">
              <p className="hub-about-eyebrow">{cms.heroEyebrow}</p>
              <h1 id="hub-about-hero-title" className="hub-about-hero__title">
                {cms.heroTitleLine1}
                {cms.heroTitleLine2 ? (
                  <>
                    <br />
                    {cms.heroTitleLine2}
                  </>
                ) : null}
              </h1>
              <p className="hub-about-hero__lead">{cms.heroLead}</p>
              <Link to={cms.heroCtaLink || "/signup"} className="hub-about-btn hub-about-btn--primary">
                {cms.heroCtaLabel || "Join Our Network"}
              </Link>
            </div>

            <div className="hub-about-hero__visual" aria-hidden>
              <div
                className="hub-about-hero__visual-handshake"
                style={
                  heroBackgroundSrc ? { backgroundImage: `url(${heroBackgroundSrc})` } : undefined
                }
              />
              {!isCustomHeroImage && heroCityOverlaySrc ? (
                <div
                  className="hub-about-hero__visual-city"
                  style={{ backgroundImage: `url(${heroCityOverlaySrc})` }}
                />
              ) : null}
              <div className="hub-about-hero__visual-fade" />
            </div>
          </div>
        </section>

        <section className="hub-about-who" aria-labelledby="hub-about-who-title">
          <div className="container-fluid hub-about-who__inner">
            <div className="hub-about-who__copy">
              <p className="hub-about-eyebrow">{cms.whoEyebrow}</p>
              <h2 id="hub-about-who-title" className="hub-about-section-title">
                {cms.whoTitle || "About Trocanet"}
              </h2>
              {cms.whoParagraph1 ? <p>{cms.whoParagraph1}</p> : null}
              {cms.whoCtaLink?.startsWith("#") ? (
                <a href={cms.whoCtaLink} className="hub-about-btn hub-about-btn--outline">
                  {cms.whoCtaLabel}
                </a>
              ) : (
                <Link to={cms.whoCtaLink || "/signup"} className="hub-about-btn hub-about-btn--outline">
                  {cms.whoCtaLabel}
                </Link>
              )}
            </div>

            <div className="hub-about-who-panel">
              <div className="hub-about-who-panel__grid">
                {(cms.whoCards || []).map((card) => {
                  const Icon = WHO_CARD_ICONS[card.key] || FaUsers;
                  return (
                    <article key={card.key || card.title} className="hub-about-who-item">
                      <span className="hub-about-who-item__icon" aria-hidden>
                        <Icon />
                      </span>
                      <div className="hub-about-who-item__copy">
                        <h3>{card.title}</h3>
                        <p>{card.desc}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        <section className="hub-about-stats" aria-label="Platform statistics">
          <div className="container-fluid hub-about-stats__wrap">
            <div className="hub-about-stats__inner">
              {STATS.map((stat) => {
                const Icon = stat.icon;
                return (
                  <article key={stat.label} className="hub-about-stat">
                    <span className="hub-about-stat__icon" aria-hidden>
                      <Icon />
                    </span>
                    <div className="hub-about-stat__text">
                      <strong>{stat.value}</strong>
                      <span>{stat.label}</span>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-about-offers" id="hub-about-offers" aria-labelledby="hub-about-offers-title">
          <div className="container-fluid hub-about-offers__inner">
            <div className="hub-about-offers__head">
              <p className="hub-about-eyebrow">What We Offer</p>
              <h2 id="hub-about-offers-title" className="hub-about-section-title">
                Everything You Need to Grow
              </h2>
            </div>
            <div className="hub-about-offers__grid">
              {OFFERS.map((offer) => {
                const Icon = offer.icon;
                return (
                  <article key={offer.title} className="hub-about-offer-card">
                    <span className="hub-about-offer-card__icon" aria-hidden>
                      <Icon />
                    </span>
                    <h3>{offer.title}</h3>
                    <p>{offer.desc}</p>
                    <Link to={offer.to} className="hub-about-offer-card__link">
                      {offer.cta} <FaArrowRight aria-hidden />
                    </Link>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-about-why" aria-labelledby="hub-about-why-title">
          <div className="container-fluid hub-about-why__inner">
            <div className="hub-about-why__head">
              <p className="hub-about-eyebrow">Why Choose Trocanet</p>
              <h2 id="hub-about-why-title" className="hub-about-section-title">
                Built for Businesses, Backed by Trust
              </h2>
            </div>
            <div className="hub-about-why__grid">
              {WHY_CHOOSE.map((item) => {
                const Icon = item.icon;
                return (
                  <article key={item.title} className="hub-about-why-card">
                    <span className="hub-about-why-card__icon" aria-hidden>
                      <Icon />
                    </span>
                    <div className="hub-about-why-card__copy">
                      <h3>{item.title}</h3>
                      <p>{item.desc}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>
        </section>

        <section className="hub-about-cta" aria-labelledby="hub-about-cta-title">
          <div className="container-fluid hub-about-cta__shell">
            <div className="hub-about-cta__inner">
              <div className="hub-about-cta__copy">
                <h2 id="hub-about-cta-title">Ready to Grow Your Business?</h2>
                <p>Join thousands of businesses already growing with Trocanet.</p>
              </div>
              <div className="hub-about-cta__actions">
                <Link to="/signup" className="hub-about-btn hub-about-btn--primary">
                  Join as a Seller
                </Link>
                <Link to="/signup" className="hub-about-btn hub-about-btn--ghost">
                  Join as a Buyer
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>

      <HubHomeFooter />
    </div>
  );
}
